import React, { useMemo, useRef, useState } from 'react';
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Crosshair,
  Pencil,
  Sparkles,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import FundingClientWorkspace from './FundingClientWorkspace';
import FundingIdentityProfileDrawer from './FundingIdentityProfileDrawer';
import AddFundingEntityDrawer from './AddFundingEntityDrawer';
import FundingSampleBanner from './FundingSampleBanner';
import { formatClientCode } from './FundingClientRosterTable';
import { formatDate, getClientInitials } from '../../../utils/fundingWorkspaceFormatters';
import {
  appendEntityToClient,
  entitySelectLabel,
  getClientEntities,
  getEntityById,
} from './fundingClientEntities';
import { resolveSelectedClientId } from '../../../services/funding/fundingClientsService';

const FundingClientDetailsView = ({
  client,
  clients = [],
  rosterStats = {},
  agencyId,
  onBack,
  onNavigateClient,
  onEdit,
  onDelete,
  onRosterStatsChange,
  onClientUpdate,
  onClearSampleData,
  onNewFundingClient,
}) => {
  const workspaceRef = useRef(null);
  const [identityOpen, setIdentityOpen] = useState(false);
  const [addEntityOpen, setAddEntityOpen] = useState(false);

  const selectedClientId = useMemo(() => resolveSelectedClientId(client), [client]);

  const clientIndex = useMemo(
    () => clients.findIndex((item) => resolveSelectedClientId(item) === selectedClientId),
    [clients, selectedClientId],
  );

  const stats = rosterStats[selectedClientId] || {};
  const total = clients.length;
  const position = clientIndex >= 0 ? clientIndex + 1 : 1;

  const entities = useMemo(() => getClientEntities(client), [client]);
  const activeEntity = useMemo(
    () => getEntityById(client, client?.selectedEntityId),
    [client],
  );

  const primaryOwner = useMemo(
    () => activeEntity?.owners?.find((owner) => owner.isPrimary) || activeEntity?.owners?.[0],
    [activeEntity],
  );

  const primaryProfile = useMemo(() => {
    if (!client) return null;
    return {
      ownerName: primaryOwner?.name || client.name,
      ownershipPercentage: primaryOwner?.ownershipPercent ?? null,
      businessLegalName: activeEntity?.businessLegalName || client.businessName,
      isPrimary: true,
    };
  }, [client, primaryOwner, activeEntity]);

  const handleEntityChange = (entityId) => {
    onClientUpdate?.({ ...client, selectedEntityId: entityId });
  };

  const handleAddEntity = (entity) => {
    onClientUpdate?.(appendEntityToClient(client, entity));
  };

  const activeEntityDisplayName = activeEntity?.dbaName
    || activeEntity?.businessLegalName
    || client.businessName
    || '—';

  if (!client) return null;

  return (
    <div className="fg-client-details-view" data-testid="funding-client-details-view">
      <div className="fg-detail-header funding-client-detail-nav">
        <div className="funding-client-detail-nav__title">
          <button
            type="button"
            className="fg-detail-back funding-back-button"
            onClick={onBack}
            data-testid="client-details-back"
            aria-label="Back to client roster"
          >
            <span className="fg-back-arrow"><ArrowLeft className="size-5" /></span>
            <span>Client Details</span>
          </button>
        </div>

        <div className="funding-client-detail-toolbar" aria-label="Client navigation and actions">
          <span
            className="funding-client-counter fg-count-pill"
            data-testid="client-details-counter"
            aria-label={`Client ${position} of ${total}`}
          >
            {position}/{total}
          </span>
          <button
            type="button"
            className="funding-client-toolbar-button fg-nav-arrow"
            aria-label="Previous client"
            data-testid="client-details-prev"
            disabled={clientIndex <= 0}
            onClick={() => onNavigateClient?.(clients[clientIndex - 1])}
          >
            <ChevronLeft className="size-5" />
          </button>
          <button
            type="button"
            className="funding-client-toolbar-button fg-nav-arrow"
            aria-label="Next client"
            data-testid="client-details-next"
            disabled={clientIndex < 0 || clientIndex >= clients.length - 1}
            onClick={() => onNavigateClient?.(clients[clientIndex + 1])}
          >
            <ChevronRight className="size-5" />
          </button>
          <button
            type="button"
            className="funding-client-toolbar-button fg-detail-icon-btn"
            aria-label="Recalculate readiness"
            onClick={() => toast.info('Readiness recalculated (mock)')}
          >
            <Crosshair className="size-4" />
          </button>
          <button
            type="button"
            className="funding-client-toolbar-button fg-detail-icon-btn"
            aria-label="AI advisor"
            onClick={() => toast.info('AI advisor (mock)')}
          >
            <Sparkles className="size-4" />
          </button>
          <button
            type="button"
            className="funding-client-toolbar-button fg-detail-icon-btn"
            aria-label="Edit client"
            onClick={() => onEdit?.(client)}
          >
            <Pencil className="size-4" />
          </button>
          <button
            type="button"
            className="funding-client-toolbar-button fg-detail-icon-btn funding-client-toolbar-button--danger danger"
            aria-label="Delete client"
            onClick={() => onDelete?.(client)}
          >
            <Trash2 className="size-4" />
          </button>
        </div>
      </div>

      {client.isSample ? (
        <FundingSampleBanner
          variant="details"
          testId="client-details-sample-banner"
          title="You're viewing a sample client workspace."
          subtitle="Create your own funding client to start using real client data, applications, documents, and funding history."
          secondaryActionLabel="Back to Client roster"
          clearActionLabel="Clear sample data"
          primaryActionLabel="New Funding Client"
          secondaryTestId="detail-sample-back-roster"
          clearTestId="detail-sample-clear"
          primaryTestId="detail-sample-new-client"
          onSecondaryAction={onBack}
          onClear={onClearSampleData}
          onPrimaryAction={onNewFundingClient}
        />
      ) : null}

      <section className="fg-client-detail-card" data-testid="funding-client-detail-header">
        <div className="fg-client-detail-main">
          <div className="fg-client-identity">
            <div className="fg-client-avatar">{client.initials || getClientInitials(client.name)}</div>

            <div className="fg-client-copy">
              <div className="fg-client-name">{client.name}</div>
              <div className="fg-client-business">{activeEntityDisplayName}</div>

              <div className="fg-client-pills">
                <span className="fg-pill">{formatClientCode(client, clientIndex)}</span>
                <span className="fg-pill fg-pill-green">{client.status || 'Draft'}</span>
                <span className="fg-pill">
                  Readiness {client.readinessScore != null ? `${client.readinessScore}%` : '—'}
                </span>
                <span className="fg-pill">
                  Last app {stats.lastApplicationDate ? formatDate(stats.lastApplicationDate) : '—'}
                </span>
                {client.isSample ? <span className="fg-pill fg-pill-blue">Sample workspace</span> : null}
                {!client.isSample && client.sourceLabel ? (
                  <span className="fg-pill">{client.sourceLabel}</span>
                ) : null}
              </div>
            </div>
          </div>

          <div className="fg-client-actions">
            <button type="button" className="fg-btn fg-btn-white" onClick={() => setIdentityOpen(true)}>
              Identity & Business
            </button>
            <button type="button" className="fg-btn fg-btn-white">Export client history</button>
            <button
              type="button"
              className="fg-btn fg-btn-green"
              data-testid="client-header-new-application"
              onClick={() => workspaceRef.current?.openNewApplication()}
            >
              New Application
            </button>
          </div>
        </div>

        <div className="fg-active-entity-strip">
          <div className="fg-active-entity-field">
            <label htmlFor="activeEntitySelect">Active Entity</label>
            {entities.length > 0 ? (
              <select
                id="activeEntitySelect"
                data-testid="active-entity-select"
                className="fg-active-entity-select"
                value={client.selectedEntityId || entities[0]?.id || ''}
                onChange={(e) => handleEntityChange(e.target.value)}
              >
                {entities.map((entity) => (
                  <option key={entity.id} value={entity.id}>
                    {entitySelectLabel(entity)}
                  </option>
                ))}
              </select>
            ) : (
              <p className="fg-entity-empty-hint" data-testid="active-entity-empty">
                No saved entity yet. Add an entity from Identity & Business.
              </p>
            )}
          </div>

          <button
            type="button"
            className="fg-add-entity-btn"
            data-testid="add-entity-btn"
            onClick={() => setAddEntityOpen(true)}
          >
            + Add Entity
          </button>

          <div className="fg-entity-meta">
            <span className="fg-pill">Type: {activeEntity?.entityType || 'Entity'}</span>
            <span className="fg-pill">Owner: {primaryOwner?.name || '—'}</span>
            <span className="fg-pill">Owners: {activeEntity?.owners?.length || 0}</span>
            {activeEntity?.einMasked ? (
              <span className="fg-pill">EIN: {activeEntity.einMasked}</span>
            ) : null}
            <span className="fg-pill fg-pill-green">{activeEntity?.status || client.status}</span>
          </div>
        </div>
      </section>

      <FundingClientWorkspace
        ref={workspaceRef}
        client={client}
        agencyId={agencyId || client.agencyId}
        embedded
        selectedEntityId={client.selectedEntityId}
        onRosterStatsChange={onRosterStatsChange}
        onOpenIdentity={() => setIdentityOpen(true)}
        onEditClient={onEdit}
      />

      <FundingIdentityProfileDrawer
        open={identityOpen}
        onOpenChange={setIdentityOpen}
        profile={primaryProfile}
        title="Identity & Business"
        onSave={async () => {
          toast.success('Identity profile saved locally');
          setIdentityOpen(false);
        }}
      />

      <AddFundingEntityDrawer
        open={addEntityOpen}
        onOpenChange={setAddEntityOpen}
        client={client}
        onSave={handleAddEntity}
      />
    </div>
  );
};

export default FundingClientDetailsView;
